<template>
  <div>

    <div class="my-3 text-2xl font-medium text-gray-500">
      <h1>Preencher Informações</h1>
    </div>

    <ExternalClientInfo :client="client" :loading="loading" />

    <div
      v-if="orderItems.length"
      class="mt-3 overflow-hidden bg-white border border-gray-200 rounded shadow-sm"
    >
      <div
        v-if="!loading && !loadingError"
        class="px-4 py-3 text-base font-medium"
      >
        Número do Pedido: {{ orderItems[0].order.cod }}
      </div>

      <ExternalLoading
        v-if="loading"
        text="Carregando número do pedido"
      />
    </div>

    <div
      v-if="orderItems.length"
      class="mt-3 overflow-hidden bg-white border border-gray-200 rounded shadow-sm">

      <div
        v-if="!loading && !loadingError"
        class="px-4 py-4 text-base font-medium"
      >
        Itens desse atendimento

        <hr class="mt-4">

        <ExternalOrderItemInfo
          v-for="(orderItem, key) in orderItems"
          :key="key"
          :order-item="orderItem"
          :selected-items-ids="[]"
          :show-checkbox="false"
          :organization="organization"
        />

      </div>

      <ExternalLoading
        v-if="loading"
        text="Carregando itens do pedido"
      />
    </div>

    <div class="py-2 my-3 bg-white border border-gray-200 rounded shadow-sm">
      <div
        v-if="!loadingError && !loading && task"
        class="px-4 py-4"
      >
        <div class="py-4">
          <div class="inline-flex items-center">
            <div
              class="w-2 h-2 rounded-full"
              :class="slaClass"
            >
            </div>
            <div class="ml-2">
              {{ slaStatus }}
            </div>
          </div>

          <span class="ml-2 inline-flex items-center bg-gray-100 text-gray-700 rounded py-1 px-2.5">Vence em: {{ limitDate }}</span>

          <span
            v-if="updated_at"
            class="ml-2 inline-flex items-center bg-gray-100 text-gray-700 rounded py-1 px-2.5">Atualizada em: {{ updated_at }}</span>
        </div>

        <strong>Descrição: </strong> {{ task.description }}

        <div class="mt-1" v-show="!sending">
          <ExternalTaskBody
            v-if="!loadingError && !loading && task"
            :task="task"
            :ticket="ticketId"
            :ticketTask="ticketTaskId"
            :sending="sending"
            :disabled="snackBar.show || !task.user"
            :available-status="availableStatus"
            :organization="organization"
          />
        </div>
      </div>

      <ExternalLoading
        v-if="sending"
        text="Enviando informações..."
      />

      <ExternalLoading
        v-if="loading"
        text="Carregando informações adicionais"
      />
    </div>

    <div
      v-if="loadingError"
      class="flex justify-center mt-3"
    >
      <button
        class="inline-flex items-center justify-center w-12 h-12 text-white rounded-full bg-purple-organization hover:bg-purple-organization-light active:bg-purple-organization-dark disabled:bg-purple-organization disabled:opacity-50"
        :disabled="snackBar.show"
        @click="init()"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
      </button>
    </div>

    <ExternalSnackbar
      :show="snackBar.show"
      @closed="snackBar.show = false"
    >
      {{ snackBar.message }}
    </ExternalSnackbar>

  </div>
</template>

<script>
import dayjs from 'dayjs'
import EventBus from '@/utils/event-bus'
import ExternalTaskBody from './ExternalTaskBody'
import ExternalClientInfo from './commons/ExternalClientInfo'
import ExternalOrderItemInfo from './commons/ExternalOrderItemInfo'
import 'dayjs/locale/pt-br'
import EnumSla from '@/enum/enum.sla'
import { SELECTABLE_STATUS_TYPES, TO_TO, DONE } from '@/enum/enum.status-types'
import { WORKFLOW } from '@/enum/enum.task-types'
import { find as findStatus } from '@/repositories/integrations/mobly/services/csExternal/status'
import { findOne as findOneUser } from '@/repositories/integrations/mobly/services/csExternal/user'
import { find as findInternalConfig } from '@/repositories/integrations/mobly/services/csExternal/internalConfig'
import { findOne as findOneCustomer, findByPathAndDocument } from '@/repositories/integrations/mobly/services/csExternal/customer'
import { findOne as findOneLead } from '@/repositories/integrations/mobly/services/csExternal/lead'
import {
  findOne as findOneTicket,
  update as updateTicket,
  findTicketTasks
} from '@/repositories/integrations/mobly/services/csExternal/ticket'
import {
  findOne as findOneTicketTaskTask,
  update as updateTicketTaskTask
} from '@/repositories/integrations/mobly/services/csExternal/ticketTaskTask'
import ExternalLoading from '@/components/commons/ExternalLoading'
import ExternalSnackbar from '@/components/commons/ExternalSnackbar'
import { MOBLY } from '@/utils/possible-organizations-values'

export default {
  name: 'ExternalTaskDetail',

  components: {
    ExternalTaskBody,
    ExternalClientInfo,
    ExternalOrderItemInfo,
    ExternalLoading,
    ExternalSnackbar
  },

  props: {
    taskId: String,
    ticketTaskId: String,
    ticketId: String,
    organization: String,
    hash: String,
    type: String
  },

  data () {
    return {
      task: {},
      startDate: null,
      limitDate: '-',
      updated_at: null,
      slaClass: 'bg-green-organization',
      slaStatus: EnumSla.ONTIME,
      loading: true,
      sending: false,
      loadingError: false,
      availableStatus: [],
      snackBar: {
        show: false,
        position: 'left',
        message: '',
        duration: 3000
      },
      taskToSave: {
        user: {},
        status: {},
        ticket: '',
        body: {}
      },
      messages: {
        error: 'Ocorreu um erro na requisição, tente novamente.',
        jsonError: 'Ocorreu um erro na validação dos campos.',
        success: 'Informações atualizadas com sucesso!',
        incomplete: 'Preencha os campos obrigatórios para continuar.'
      },
      clientId: null,
      customerId: null,
      user: {},
      client: {},
      orderItems: [],
      isLead: false
    }
  },

  mounted () {
    this.init()
    EventBus.$on('saveForm', finished => this.saveTask(finished))
  },

  methods: {
    async init () {
      this.loading = true
      this.loadingError = false

      await this.setTicketInfo()
      await this.setCustomer()
      if (!(this.client && this.client.document)) {
        this.$router.push('/notFound')
        return
      }

      if (this.organization === MOBLY && !this.isLead) {
        try {
          const statusCustomerResponse = await findByPathAndDocument(this.client.document, this.organization)
          const statusCustomer = statusCustomerResponse.data
          const { status } = statusCustomer

          if (!status) {
            this.$router.push('/notFound')
            return
          }
          if (status === 'blocked' || status === 'inactive') {
            this.$router.push('/blocked')
            return
          }
        } catch (e) {
          console.error(e)
        }
      }

      const requestInfo = await this.requestInfo()
      if (!requestInfo) {
        this.$router.push('/notFound')
        return
      }

      await this.doTaskSLA()
      await this.setUser()
      await this.updateTaskUser()
      await this.updateTaskStatus()

      this.selectTaskStatus()
      this.setDates()

      this.loading = false
    },

    async requestInfo () {
      const taskData = await findOneTicketTaskTask(this.ticketTaskId, this.taskId, '', this.organization)
        .then(response => response.data).catch(() => false)

      if (!taskData || taskData.doneAt || taskData.canceled_at) return false

      const status = taskData.taskType.type === 'workflow' ? DONE : SELECTABLE_STATUS_TYPES

      const statusParams = `types=${status}`
      const statusData = await findStatus(statusParams, this.organization)
        .then(response => response.data).catch(() => false)

      if (!statusData) return false

      this.task = taskData
      this.availableStatus = statusData

      return true
    },

    async doTaskSLA () {
      const { task } = this
      const start = task.startedAt

      this.startDate = dayjs(start).format('DD/MM/YYYY HH:mm')
      this.limitDate = dayjs(task.dueBy).format('DD/MM/YYYY HH:mm')

      const isLate = dayjs().isAfter(task.dueBy)

      if (!isLate) return

      this.slaClass = 'bg-red-organization'
      this.slaStatus = EnumSla.LATE
    },

    async setUser () {
      // Get UserConfig
      const configParams = 'cod=mobly-external-user'
      const configResponse = await findInternalConfig(configParams, this.organization)
      const userId = configResponse.data.docs[0].config._id

      // Get User
      const userParams = ''
      const userResponse = await findOneUser(userId, userParams, this.organization)
      this.user = userResponse.data
    },

    async setTicketInfo () {
      const ticketResponse = await findOneTicket(this.ticketId, '', this.organization)
      if (ticketResponse.data.customer) {
        this.clientId = ticketResponse.data.customer._id
        this.isLead = false
      } else if (ticketResponse.data.lead) {
        this.clientId = ticketResponse.data.lead._id
        this.isLead = true
      }
      this.orderItems = ticketResponse.data.orderItem
    },

    async setCustomer () {
      const clientParams = `fields=_id,name,document&hash=${this.hash}&ticketId=${this.ticketId}`
      try {
        if (this.isLead) {
          const clientResponse = await findOneLead(this.clientId, clientParams, this.organization)
          this.client = clientResponse.data
        } else {
          const clientResponse = await findOneCustomer(this.clientId, clientParams, this.organization)
          this.client = clientResponse.data
        }
      } catch (err) {
        console.log('err', err)
        if (err.response && err.response.status === 401) {
          window.location.href = '/unauthorized'
        }
      }
    },

    updateTaskUser () {
      if (this.task.user) return

      const userGroups = this.user.groups.map(element => element.group._id)

      if (userGroups.includes(this.task.group._id)) {
        this.task.user = this.user
        this.task.selectedStatus = this.availableStatus[0]._id
        if (this.task.taskType.type !== 'workflow') this.saveTask(false)
      }
    },

    updateTaskStatus () {
      if (!this.task.user) return
      if (this.task.status.length >= 2) return
      this.task.selectedStatus = this.availableStatus[0]._id
      if (this.task.taskType.type !== 'workflow') this.saveTask(false)
    },

    selectTaskStatus () {
      if (this.task.status.length < 2) return

      const lastStatus = this.task.status[this.task.status.length - 1]
      let currentStatus = lastStatus._id

      if (lastStatus.statusType.type === TO_TO) currentStatus = this.availableStatus[0]._id

      this.task.selectedStatus = currentStatus
    },

    async saveTask (finished) {
      this.sending = true
      this.snackBar.message = null

      if (finished && !this.readyToFinish()) {
        return this.setSnackError(this.messages.incomplete)
      }

      if (finished) {
        const status = this.availableStatus.find(
          status => status.statusType.type === DONE && status.fallback && status.name !== 'Cancelado'
        )
        this.task.selectedStatus = status._id
      }

      this.taskToSave.body = await this.formatBody()

      if (!this.taskToSave.body) return this.setSnackError(this.messages.jsonError)

      this.taskToSave.user = { _id: this.user._id }
      this.taskToSave.status = this.task.status[0]._id
      this.taskToSave.ticket = this.ticketId

      if (this.task.selectedStatus) this.taskToSave.status = { _id: this.task.selectedStatus }

      const savedTask = await updateTicketTaskTask(
        this.ticketTaskId, this.taskId, this.taskToSave, this.organization
      ).then(response => response.data._id).catch(() => false)

      if (!savedTask) return this.setSnackError(this.messages.error)

      const fields = await this.getSharedFields()

      const ticketShared = {
        shared: [
          {
            _id: this.taskId,
            fields
          }
        ]
      }

      const saveTicket = await updateTicket(ticketShared, this.ticketId, this.organization)
        .then(response => response.data._id).catch(() => false)

      if (!saveTicket) return this.setSnackError(this.messages.error)

      this.updateInfo()

      EventBus.$emit('update-ticket')

      if (finished) await this.autoPopTask()

      this.sending = false

      return true
    },

    async autoPopTask () {
      const taskTransitions = this.task.transitions
      const taskType = this.task.taskType.type

      // Se a task não tem transitions e não é workflow já retorna
      if (!taskTransitions.length && taskType !== WORKFLOW) {
        this.$router.push('/taskUpdated')
        return
      }

      const params = 'sort=created_at&limit=100&doneAt=null'
      const { data: { docs: ticketTasks } } = await findTicketTasks(
        this.ticketId, params, this.organization
      )

      // Se não tiver nenhum ticketTask em aberto retorna
      if (!ticketTasks.length) {
        this.$router.push('/taskUpdated')
        return
      }

      const userGroups = this.user.groups.map(group => group.group._id)

      let autopopTask = null

      const toDoStatuses = await findStatus('types=to_do&fields=_id', this.organization)
      const toDoStatusesIds = toDoStatuses.data.map(status => status._id)

      ticketTasks.forEach(ticketTask => {
        if (autopopTask) return
        ticketTask.tasks.forEach(task => {
          if (autopopTask) return
          if (
            task.auto_pop &&
            userGroups.includes(task.group) &&
            toDoStatusesIds.includes(task.status[task.status.length - 1])
          ) {
            task.ticketTaskId = ticketTask._id
            autopopTask = task
          }
        })
      })

      if (!autopopTask) {
        this.$router.push('/taskUpdated')
        return
      }

      const ticketTaskId = autopopTask.ticketTaskId
      const taskId = autopopTask._id

      this.$router.push(`/taskDetail?h=${this.hash}&org=${this.organization}&ticket=${this.ticketId}&task=${taskId}&ticketTask=${ticketTaskId}&type=${this.type}`)

      this.$emit('refresh')
    },

    updateInfo () {
      const currentStatus = this.availableStatus.find(
        status => status._id === this.task.selectedStatus
      )

      this.updated_at = dayjs().format('DD/MM/YYYY HH:mm')
      if (this.task.status[this.task.status.length - 1]._id !== currentStatus._id) {
        this.task.status.push(currentStatus)
      }

      this.doTaskSLA()
    },

    setSnackError (error) {
      this.snackBar.message = error
      this.snackBar.show = true
      this.sending = false

      return false
    },

    async getSharedFields () {
      const fields = []
      let email = ''

      if (!this.task.body) return fields

      if (this.task.taskType.type === 'email') {
        const { data: { emails } } = await findOneTicket(this.ticketId, '', this.organization)
        email = emails[0].to
      }

      this.task.body.sections.forEach((section) => {
        section.forms.forEach((form) => {
          form.fields.forEach((field) => {
            if (field.name === 'to') field.value = email
            if (field.shared && field.value && field.value.length > 0) fields.push(field)
          })
        })
      })

      return fields
    },

    readyToFinish () {
      const lastStatus = this.task.status[this.task.status.length - 1]

      if (lastStatus.statusType.type === DONE) return true

      const { sections } = this.task.body
      const invalidSectionsFields = sections.map(section => this.checkInvalidForms(section))

      return !invalidSectionsFields.includes(true)
    },

    checkInvalidForms (section) {
      const invalidFormFields = section.forms.map(form => this.checkInvalidFields(form))

      return invalidFormFields.includes(true)
    },

    checkInvalidFields (form) {
      const invalidFields = form.fields.map((field) => {
        const multi = ['checkbox', 'switch']

        if (field.type === 'file') {
          /**
           * If you can see a file field and is not optional and,
           *  don't have de min and max uploads.
           */
          return field.show &&
          field.showFromBack &&
          !field.optional &&
          (!field.value ||
          !field.value.uploads ||
          field.value.uploads.length < field.minFileNumber ||
          field.value.uploads.length > field.maxFileNumber)
        }

        if (multi.includes(field.type)) {
          /**
           * If you can see a field and is not optional and is one of multi list,
           *  it should have length diff than 0.
           */
          return field.show &&
          field.showFromBack &&
          !field.optional &&
          field.value.length === 0
        }

        /**
         * If you can see a field and is not optional,
         *  it should have a value.
         */
        return field.show &&
        field.showFromBack &&
        !field.optional &&
        !field.value
      })

      return invalidFields.includes(true)
    },

    setDates () {
      if (this.task.updated_at) {
        this.updated_at = dayjs(this.task.updated_at).format('DD/MM/YYYY HH:mm')
      }
    },

    formatBody () {
      if (!this.task.body) return {}

      let formattedBody

      try {
        formattedBody = JSON.parse(JSON.stringify(this.task.body))
      } catch (error) {
        return false
      }

      return formattedBody
    }
  }
}
</script>
